import {
  CenteredContainer,
  Column,
  Container,
  ElasticContentContainer,
  EmptyScreen,
  Row
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import type { AdminReferenceDocument } from '@cdab/scania/qpr/schema'
import { Roles } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import {
  capitalizeAllFirstLetters,
  capitalizeFirstLetter,
  formatISODate
} from '@cdab/utils'
import {
  TdsBodyCell,
  TdsHeaderCell,
  TdsIcon,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
  TdsTableToolbar
} from '@scania/tegel-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useNavigate,
  useRouteError
} from 'react-router'
import { StyledTdsButton } from './documents.styles'

export type AdminDocumentsLoaderData = {
  documents: AdminReferenceDocument[]
}

type PreparedDocumentData = {
  market: string
  logicalFileName: string
  physicalFileName: string | null
  auditPoints: string
  checkpoints: string
  updatedTime: string
  documentId: number
}

export async function loader(): Promise<AdminDocumentsLoaderData> {
  const client = getClient()

  const userInfo = await client.GetuserInfo()
  if (!userInfo) {
    throw json({
      status: 401
    })
  }

  const user = await client.UserService.GetUser(userInfo.userId)
  if (user.role < Roles.CoOrdinator) {
    throw json({
      status: 403
    })
  }

  try {
    const [documents] = await Promise.all([
      client.DocumentService.GetDocuments()
    ])

    return {
      documents
    }
  } catch (error) {
    if (error instanceof Response) {
      throw new Response(null, {
        status: error.status
      })
    } else {
      throw new Response(null, {
        status: 404
      })
    }
  }
}

function prepareTableData(
  data: AdminReferenceDocument[]
): PreparedDocumentData[] {
  const preparedData = data
    .sort((a: AdminReferenceDocument, b: AdminReferenceDocument) =>
      b.updatedTime.localeCompare(a.updatedTime)
    )
    .map(document => {
      return {
        market: document.market ? document.market.marketName : 'Factory',
        logicalFileName: document.title,
        physicalFileName: document.physicalDocumentFileName ?? document.url,
        auditPoints: document.auditPoints
          .map(auditPoint => auditPoint.auditPointNo)
          .join(', '),
        checkpoints: document.checkPoints
          .map(checkPoint => checkPoint.checkPointNo)
          .join(', '),
        updatedTime: formatISODate(new Date(document.updatedTime)),
        documentId: document.id
      }
    })

  return preparedData
}

export function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'documents'])
  const { updateTitles } = useTitle()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const { documents } = useLoaderData() as AdminDocumentsLoaderData

  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<PreparedDocumentData[]>([])

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.columnKey) {
        setSortConfig(null)
        return
      }
      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }
      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  const onDocumentRowClick = (documentId: string) => {
    if (documentId) {
      navigate(`/admin/documents/${documentId}`)
    }
  }

  useEffect(() => {
    let data = prepareTableData(documents)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig)
    }

    setProcessedData(data)
  }, [documents, sortConfig, filterQuery])

  useEffect(() => {
    updateTitles({
      contentHeader: {
        subtitle: capitalizeAllFirstLetters(
          t('reference-documents', { ns: 'common' })
        ),
        title: t('administration')
      },
      mobile: {
        title: `${capitalizeAllFirstLetters(
          t('reference-documents', { ns: 'common' })
        )}`,
        description: null
      }
    })
  }, [t, updateTitles])

  return (
    <ElasticContentContainer scrollY overflowHidden>
      <Container fluid='normal'>
        <Row>
          <Column width={12} padding={isLg}>
            <StyledTdsButton
              disabled={false}
              text={t('add-new')}
              onClick={() => navigate('/admin/documents/new')}
              size='md'
            >
              <TdsIcon slot='icon' name='document_check' />
            </StyledTdsButton>
          </Column>
          <Column width={12} padding={isLg} scrollX>
            <TdsTable
              compactDesign={false}
              noMinWidth={!isLg}
              modeVariant='secondary'
            >
              <TdsTableToolbar
                tableTitle={capitalizeAllFirstLetters(
                  t('reference-documents', { ns: 'common' })
                )}
                filter
                onTdsFilter={handleTableFilter}
              />
              <TdsTableHeader>
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='market'
                  cellValue={capitalizeFirstLetter(t('market'))}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='logicalFileName'
                  cellValue={t('logical-file-name', { ns: 'documents' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='physicalFileName'
                  cellValue={t('physical-file-name', { ns: 'documents' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='auditPoints'
                  cellValue={capitalizeFirstLetter(t('audit-points'))}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='checkPoints'
                  cellValue={capitalizeFirstLetter(t('checkpoints'))}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='updatedTime'
                  cellValue={t('updated-date', { ns: 'documents' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='documentId'
                  cellValue={'ID'}
                  sortable
                />
              </TdsTableHeader>
              <TdsTableBody>
                {processedData.map(row => (
                  <TdsTableBodyRow
                    key={row.documentId}
                    onClick={() =>
                      onDocumentRowClick(row.documentId.toString())
                    }
                  >
                    <TdsBodyCell cellKey='market'>{row.market}</TdsBodyCell>
                    <TdsBodyCell cellKey='logicalFileName'>
                      {(row.logicalFileName ?? '').length > 50
                        ? row.logicalFileName?.substring(0, 50) + '...'
                        : row.logicalFileName}
                    </TdsBodyCell>
                    <TdsBodyCell cellKey='physicalFileName'>
                      {(row.physicalFileName ?? '').length > 50
                        ? row.physicalFileName?.substring(0, 50) + '...'
                        : row.physicalFileName}
                    </TdsBodyCell>
                    <TdsBodyCell cellKey='auditPoints'>
                      {row.auditPoints.length > 50
                        ? row.auditPoints.substring(0, 50) + '...'
                        : row.auditPoints}
                    </TdsBodyCell>
                    <TdsBodyCell cellKey='checkPoints'>
                      {row.checkpoints.length > 50
                        ? row.checkpoints.substring(0, 50) + '...'
                        : row.checkpoints}
                    </TdsBodyCell>
                    <TdsBodyCell cellKey='updatedTime'>
                      {row.updatedTime}
                    </TdsBodyCell>
                    <TdsBodyCell cellKey='documentId'>
                      {row.documentId}
                    </TdsBodyCell>
                  </TdsTableBodyRow>
                ))}
              </TdsTableBody>
            </TdsTable>
          </Column>
        </Row>
      </Container>
    </ElasticContentContainer>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    if (error.status === 403) {
      message = t('access-denied')
    }

    return (
      <CenteredContainer
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column fullHeight width={12} padding={false}>
            <EmptyScreen
              title={t('could-not-load-page')}
              description={message}
              icon={<TdsIcon name='warning' size='lg' />}
            />
          </Column>
        </Row>
      </CenteredContainer>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
